import { computed, inject, Injectable, NgZone, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CookiesService } from '../../services/cookies';
import { Notification } from '../../services/notification';
import { PermissionsService } from '../../services/permissions';
import { readExpMs } from './jwt.util';
import {
  SESSION_CONFIG,
  SessionEndReason,
  SessionStatus,
} from './session.model';
import { TokenRefreshService } from './token-refresh.service';

/** setTimeout overflows past 2^31-1 ms and fires immediately, so long waits are chunked. */
const MAX_TIMEOUT_MS = 2_147_483_000;

/**
 * The `timedLogout` session lifecycle.
 *
 * Arms a countdown from the access token's `exp`. When it fires, the session is
 * refreshed if the user has been active inside the idle window, and otherwise
 * ends in a full logout. Also the single owner of the forced-logout path, which
 * the HTTP interceptor calls when a refresh is rejected.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly config = inject(SESSION_CONFIG);
  private readonly storage = inject(CookiesService);
  private readonly permissions = inject(PermissionsService);
  private readonly notify = inject(Notification);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly refresher = inject(TokenRefreshService);

  private readonly _status = signal<SessionStatus>('inactive');
  private readonly _expiresAt = signal<number | null>(null);

  readonly status = this._status.asReadonly();
  readonly expiresAt = this._expiresAt.asReadonly();
  readonly isAuthenticated = computed(
    () => this._status() === 'active' || this._status() === 'refreshing',
  );

  // Plain fields, not signals: these are written on every user gesture and
  // nothing renders from them, so making them signals would dirty the root view
  // and schedule change detection for nothing.
  private timer: ReturnType<typeof setTimeout> | null = null;
  private listenersAttached = false;
  private lastActivityAt = 0;
  private refreshTimestamps: number[] = [];

  /**
   * Arms the session. Safe to call repeatedly — re-arms from the current token.
   *
   * Called on login, after the admin SSO handoff, and from the APP_INITIALIZER
   * so a page reload re-arms off the token still in storage. It must never
   * navigate: the initializer runs before the Router is bootstrapped. Guards and
   * the interceptor's 401 handler cover the already-expired case.
   */
  start(token?: string): void {
    const accessToken = token || this.storage.get('token');
    if (!accessToken) return;

    const exp = readExpMs(accessToken);
    if (exp === null || Date.now() >= exp) {
      this._status.set('inactive');
      return;
    }

    this.lastActivityAt = Date.now();
    this.refreshTimestamps = [];
    this._status.set('active');
    this.attachListeners();
    this.schedule();
  }

  /** Tears down timers and listeners. No navigation, no toast. */
  stop(): void {
    this.clearTimer();
    this.detachListeners();
    this.refreshTimestamps = [];
    this._expiresAt.set(null);
    if (this._status() !== 'terminated') this._status.set('inactive');
  }

  /**
   * Ends the session for good and sends the user to login.
   *
   * Idempotent via the `terminated` guard, so N concurrent 401s produce exactly
   * one logout. Deliberately does NOT call `POST /api/Auth/logout`: the access
   * token is dead by definition, a failed refresh is already the server telling
   * us the session is over, and a 401 raised from inside the logout path is the
   * recursion we are trying to avoid.
   */
  forceLogout(reason: SessionEndReason): void {
    if (this._status() === 'terminated') return;
    this._status.set('terminated');

    this.stop();
    this.refresher.reset();
    this.permissions.clearPermissions();
    this.storage.clearAll();

    if (reason === 'idle') {
      this.notify.info(
        'Session ended',
        'You were signed out after a period of inactivity.',
      );
    } else if (reason !== 'manual' && reason !== 'remote') {
      this.notify.info(
        'Session expired',
        'Your session has expired. Please sign in again.',
      );
    }

    this.router.navigate([this.config.loginRoute], { replaceUrl: true });
  }

  /**
   * Re-checks the session against the wall clock and re-arms.
   *
   * setTimeout does not run while the machine is suspended and is throttled in
   * background tabs, so a timer set for 14 minutes can fire hours late. Called
   * on tab focus and on regaining connectivity.
   */
  reconcile(): void {
    const status = this._status();
    if (status !== 'active') return;

    const exp = readExpMs(this.storage.get('token'));
    if (exp === null) {
      this.forceLogout('no-token');
      return;
    }

    if (Date.now() >= exp - this.config.refreshSkewMs) {
      this.onCycleEnd();
      return;
    }

    this.schedule();
  }

  private schedule(): void {
    this.clearTimer();

    const exp = readExpMs(this.storage.get('token'));
    if (exp === null) {
      this.forceLogout('no-token');
      return;
    }
    this._expiresAt.set(exp);

    const target = exp - Date.now() - this.config.refreshSkewMs;
    const delay = Math.max(target, this.config.minDelayMs);
    const wait = Math.min(delay, MAX_TIMEOUT_MS);
    const isChunk = wait < delay;

    // Outside the zone so the timer does not schedule a change-detection pass
    // on every fire; re-entered below because onCycleEnd writes signals,
    // navigates, and starts an HTTP call whose result must render.
    this.zone.runOutsideAngular(() => {
      this.timer = setTimeout(() => {
        if (isChunk) {
          this.schedule();
          return;
        }
        this.zone.run(() => this.onCycleEnd());
      }, wait);
    });
  }

  private onCycleEnd(): void {
    if (this._status() === 'terminated') return;

    if (!this.storage.get('token')) {
      this.forceLogout('remote');
      return;
    }

    if (!this.wasRecentlyActive()) {
      this.forceLogout('idle');
      return;
    }

    if (!this.consumeRefreshBudget()) {
      console.error('[session] refresh budget exhausted — ending session');
      this.forceLogout('refresh-failed');
      return;
    }

    this._status.set('refreshing');
    this.refresher.refresh().subscribe({
      next: () => {
        if (this._status() === 'terminated') return;
        this._status.set('active');
        this.schedule();
      },
      error: () => this.forceLogout('refresh-failed'),
    });
  }

  private wasRecentlyActive(): boolean {
    return Date.now() - this.lastActivityAt <= this.config.idleWindowMs;
  }

  /** Circuit breaker against a pathological refresh loop. */
  private consumeRefreshBudget(): boolean {
    const now = Date.now();
    this.refreshTimestamps = this.refreshTimestamps.filter(
      (at) => now - at < this.config.refreshBudgetWindowMs,
    );
    if (this.refreshTimestamps.length >= this.config.maxRefreshesPerWindow) {
      return false;
    }
    this.refreshTimestamps.push(now);
    return true;
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  // Arrow properties so the reference stays stable for removeEventListener.
  private readonly onActivity = (): void => {
    const now = Date.now();
    if (now - this.lastActivityAt < this.config.activityThrottleMs) return;
    this.lastActivityAt = now;
  };

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') return;
    // lastActivityAt is deliberately NOT bumped here: with a 15-minute idle
    // window, waking a laptop after hours away should be classified as idle.
    this.zone.run(() => this.reconcile());
  };

  private readonly onOnline = (): void => {
    this.zone.run(() => this.reconcile());
  };

  /** Another tab rotated the token or logged out — localStorage is shared. */
  private readonly onStorage = (event: StorageEvent): void => {
    if (event.key !== null && event.key !== 'token') return;

    this.zone.run(() => {
      if (!this.storage.get('token')) {
        if (this._status() !== 'terminated' && this._status() !== 'inactive') {
          this.forceLogout('remote');
        }
        return;
      }
      if (this._status() !== 'active') return;
      this.schedule();
    });
  };

  private attachListeners(): void {
    if (this.listenersAttached || typeof document === 'undefined') return;
    this.listenersAttached = true;

    // Outside the zone: without this, every keystroke and scroll event would
    // trigger a change-detection pass for a handler that only writes a number.
    this.zone.runOutsideAngular(() => {
      for (const event of this.config.activityEvents) {
        // Capture phase so a component's stopPropagation() cannot starve the
        // tracker and get the user logged out mid-work.
        document.addEventListener(event, this.onActivity, {
          passive: true,
          capture: true,
        });
      }
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('online', this.onOnline);
      window.addEventListener('storage', this.onStorage);
    });
  }

  private detachListeners(): void {
    if (!this.listenersAttached || typeof document === 'undefined') return;
    this.listenersAttached = false;

    for (const event of this.config.activityEvents) {
      document.removeEventListener(event, this.onActivity, { capture: true });
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('storage', this.onStorage);
  }
}
